import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { EmptyBudgetState } from '../components/EmptyBudgetState';
import { ExportCSVButton } from '../components/ExportCSVButton';
import { DeleteBudgetButton } from '../components/DeleteBudgetButton';
import { ViewBudgetButton } from '../components/ViewBudgetButton';
import { DuplicateBudgetButton } from '../components/DuplicateBudgetButton';
import { useAllBudgets, useDefaultBudgetId, useSetDefaultBudget } from '../hooks/useBudgetQueries';
import { getCurrentLanguage } from '../i18n';

export const BudgetList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: budgets, isLoading } = useAllBudgets();
  const { data: defaultBudgetId } = useDefaultBudgetId();
  const setDefaultBudgetMutation = useSetDefaultBudget();
  const currentLanguage = getCurrentLanguage();

  const handleRowClick = (budgetId: number) => {
    navigate(`/budgets/${budgetId}`);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-32 w-32 animate-spin rounded-full border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-8 lg:px-8">
      <div className="mb-4 sm:mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
          {t('navigation.history')}
        </h1>
      </div>

      {!budgets || budgets.length === 0 ? (
        <EmptyBudgetState />
      ) : (
        <div className="overflow-hidden bg-white shadow dark:bg-gray-800 sm:rounded-md">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {budgets?.map((budget) => (
              <li
                key={budget.id}
                className="cursor-pointer px-4 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 lg:px-6"
                onClick={(e) => {
                  // Only navigate if the click was directly on the li or on elements that aren't interactive
                  if (
                    e.target === e.currentTarget ||
                    !(e.target as HTMLElement).closest('button')
                  ) {
                    if (budget.id) handleRowClick(budget.id);
                  }
                }}
              >
                <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
                  <div className="flex items-start gap-3 lg:items-center">
                    {budget.id === defaultBudgetId ? (
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-yellow-50 dark:bg-yellow-900/20">
                        <StarIconSolid className="h-5 w-5 text-yellow-400" />
                      </div>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (budget.id) {
                            setDefaultBudgetMutation.mutate(budget.id);
                          }
                        }}
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gray-50 text-gray-400 hover:text-yellow-400 dark:bg-gray-700/50 hover:dark:bg-yellow-900/20"
                        title={t('budget.setDefault')}
                      >
                        <StarIcon className="h-5 w-5" />
                      </button>
                    )}
                    <div className="min-w-0 flex-1">
                      <h2 className="truncate text-lg font-medium text-gray-900 dark:text-white">
                        {budget.name}
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(budget.date).toLocaleDateString(currentLanguage, {
                          year: 'numeric',
                          month: 'long',
                        })}
                      </p>
                    </div>
                  </div>
                  <div
                    className="mt-3 flex flex-wrap items-center gap-2 lg:mt-0 lg:gap-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {budget.id && (
                      <>
                        <ViewBudgetButton budgetId={budget.id} />
                        <DuplicateBudgetButton budget={{ ...budget, id: budget.id }} />
                        <DeleteBudgetButton budgetId={budget.id} showText={false} />
                        <ExportCSVButton budget={{ ...budget, id: budget.id }} />
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
